import React, { ReactNode } from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useIsNarrow } from '../../hooks/useIsMobile';

export interface ResponsiveTableColumn<T> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  /** Custom cell renderer. Falls back to row[key] when omitted. */
  renderCell?: (row: T) => ReactNode;
  /** Hidden on mobile-card view. Set true to skip in mobile render. */
  hideOnMobile?: boolean;
}

interface Props<T> {
  rows: T[];
  columns: ResponsiveTableColumn<T>[];
  rowKey: (row: T) => string;
  /**
   * Mobile card renderer. Receives the row + a helper to render any specific column.
   * If omitted, falls back to a default key/value stack — fine for simple cases.
   */
  renderMobileCard?: (row: T, helpers: { renderColumn: (key: string) => ReactNode }) => ReactNode;
  /** Optional empty state. Defaults to a centered "No rows" message. */
  emptyState?: ReactNode;
  /** Show a header row in the table view. Default true. */
  showTableHeader?: boolean;
}

export function ResponsiveTable<T>({
  rows,
  columns,
  rowKey,
  renderMobileCard,
  emptyState,
  showTableHeader = true,
}: Props<T>) {
  const isNarrow = useIsNarrow();

  if (rows.length === 0) {
    return (
      <Box p={4} textAlign="center">
        {emptyState ?? <Typography color="text.secondary">No rows to display.</Typography>}
      </Box>
    );
  }

  // ----- Mobile: card stack -----
  if (isNarrow) {
    const renderColumn = (row: T, key: string): ReactNode => {
      const col = columns.find((c) => c.key === key);
      if (!col) return null;
      if (col.renderCell) return col.renderCell(row);
      const value = (row as unknown as Record<string, unknown>)[key];
      return value == null ? '—' : String(value);
    };

    return (
      <Stack spacing={1.5} p={1.5}>
        {rows.map((row) => (
          <Box
            key={rowKey(row)}
            sx={{
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {renderMobileCard ? (
              renderMobileCard(row, { renderColumn: (key) => renderColumn(row, key) })
            ) : (
              <Stack spacing={0.75}>
                {columns
                  .filter((c) => !c.hideOnMobile)
                  .map((col) => (
                    <Box key={col.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {col.label}
                      </Typography>
                      <Box sx={{ textAlign: 'right', minWidth: 0 }}>
                        {col.renderCell ? col.renderCell(row) : (
                          <Typography variant="body2">
                            {(() => {
                              const v = (row as unknown as Record<string, unknown>)[col.key];
                              return v == null ? '—' : String(v);
                            })()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    );
  }

  // ----- Desktop: real Table -----
  return (
    <TableContainer>
      <Table size="small">
        {showTableHeader && (
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align} width={col.width}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {rows.map((row) => (
            <TableRow key={rowKey(row)} hover>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align}>
                  {col.renderCell
                    ? col.renderCell(row)
                    : ((row as unknown as Record<string, unknown>)[col.key] as ReactNode) ?? '—'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ResponsiveTable;
